import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Leaf } from 'lucide-react';
import { supabase } from '../lib/supabase';

export function MembershipImportErrorLog() {
  const navigate = useNavigate();
  const [errors, setErrors] = useState<any[]>([]);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchErrors();
  }, [sortDirection]);

  const fetchErrors = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('membership_import_errors')
      .select('*')
      .order('import_timestamp', { ascending: sortDirection === 'asc' })
      .limit(200);

    if (error) console.error('Error fetching errors:', error);
    else setErrors(data);

    setLoading(false);
  };

  const toggleSort = () => {
    setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Leaf className="h-6 w-6 text-green-600" />
              <div className="ml-2">
                <span className="text-xl font-semibold text-gray-900">THRIVE</span>
                <span className="ml-2 text-sm text-gray-500">Error Log</span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-4 sm:px-0">
          <div className="flex items-center mb-6">
            <button
              onClick={() => navigate('/membership-activity-import')}
              className="mr-4 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Membership Import Error Log</h1>
          </div>

          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-blue-600 opacity-90"></div>
              <div className="relative px-8 py-10">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-white mb-2">THRIVE</h1>
                  <p className="text-green-100 text-lg mb-2">Tracking Health and Reducing Illness Via Efficiency</p>
                  <div className="w-16 h-1 bg-white/30 mx-auto rounded-full"></div>
                  <h2 className="text-2xl font-semibold text-white mt-4">Import Error Review</h2>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Recent Import Errors</h3>
                <button
                  onClick={toggleSort}
                  className="text-sm text-indigo-600 hover:underline"
                >
                  Sort by Date ({sortDirection === 'desc' ? 'Newest' : 'Oldest'})
                </button>
              </div>

              {loading ? (
                <p className="text-gray-500">Loading errors...</p>
              ) : (
                <div className="overflow-x-auto max-h-[500px] border border-gray-200 rounded-md">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-100 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Timestamp</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Reason</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Row Data</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {errors.map((err) => (
                        <tr key={err.id}>
                          <td className="px-4 py-2 text-gray-700 whitespace-nowrap">
                            {new Date(err.import_timestamp).toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-red-600 max-w-xs break-words">{err.reason}</td>
                          <td className="px-4 py-2 text-gray-600 text-xs whitespace-pre-wrap">
                            <pre>{JSON.stringify(err.row_data, null, 2)}</pre>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
